import type { TFunction } from 'i18next';

/**
 * EAA-1823: human-readable presentation of config keys and values.
 *
 * The API speaks in Django settings names (`OIDC_SID_DENYLIST_TTL`), which are
 * the right identifiers for the wire and useless as a label — a column of
 * SCREAMING_SNAKE tells an operator nothing about what the row does. So the
 * table leads with a translated name and keeps the raw key beneath it: the name
 * is what you read, the key is what you quote in a ticket or grep for in
 * settings.
 *
 * Labels live in the FE locale files rather than being served by the API,
 * because they are display copy in two languages, not part of the contract.
 * That is a deliberate difference from `EDITABLE_SPEC`, which IS served — the
 * spec changes behaviour (which editor, which bounds) and must never drift,
 * whereas a missing label degrades to showing the raw key, which is exactly
 * what the screen showed before and is survivable.
 */

/** The translated name for a key, falling back to the key itself. */
export function settingLabel(key: string, t: TFunction): string {
    const translated = t(`console.config.keys.${key}`, { defaultValue: '' });
    return translated || key;
}

type DurationUnit = 'seconds' | 'minutes' | 'hours';

/**
 * The time unit a numeric key is expressed in, or null if it is a plain count.
 *
 * Derived from the key's own name wherever it says so, so a new `*_TTL` or
 * `*_HOURS` setting is formatted correctly the day it is added rather than the
 * day someone remembers to extend a list. `OIDC_LEEWAY` is the one key whose
 * name does not carry its unit.
 */
export function durationUnit(key: string): DurationUnit | null {
    if (key === 'OIDC_LEEWAY') return 'seconds';
    if (key.endsWith('_TTL') || key.endsWith('_SECONDS')) return 'seconds';
    if (key.endsWith('_MINUTES')) return 'minutes';
    if (key.endsWith('_HOURS')) return 'hours';
    return null;
}

/**
 * "3600 seconds" as "1 hour" — the raw number still leads, because that is what
 * the operator types into the editor and what the server stores; the phrase in
 * brackets is only there so nobody has to divide by 3600 in their head.
 */
export function formatDuration(value: number, unit: DurationUnit, t: TFunction): string {
    const seconds = unit === 'seconds' ? value : unit === 'minutes' ? value * 60 : value * 3600;

    if (seconds <= 0) return '';
    if (seconds % 86400 === 0) return t('console.config.duration.days', { count: seconds / 86400 });
    if (seconds % 3600 === 0) return t('console.config.duration.hours', { count: seconds / 3600 });
    if (seconds % 60 === 0) return t('console.config.duration.minutes', { count: seconds / 60 });
    return t('console.config.duration.seconds', { count: seconds });
}

/**
 * How a live value should read in the table.
 *
 * Booleans become the same on/off words the editor offers, so the row and its
 * select agree. An empty string or empty list is called out as empty rather
 * than rendering as blank space, which is indistinguishable from a bug.
 */
export function displayValue(key: string, value: unknown, t: TFunction): string {
    if (value === null || value === undefined) return t('console.config.valueUnset');
    if (typeof value === 'boolean') {
        return value ? t('console.config.boolTrue') : t('console.config.boolFalse');
    }
    if (Array.isArray(value)) {
        return value.length === 0 ? t('console.config.valueEmptyList') : value.join('\n');
    }
    if (typeof value === 'string') {
        return value.trim() === '' ? t('console.config.valueEmpty') : value;
    }
    if (typeof value === 'number') {
        const unit = durationUnit(key);
        if (unit) {
            const phrase = formatDuration(value, unit, t);
            return phrase ? `${value} (${phrase})` : String(value);
        }
        return String(value);
    }
    return JSON.stringify(value);
}
